import clsx from "clsx"
import { useTheme } from "next-themes"
import { Fragment, useState } from "react"

import { useCollaborationColors } from "@hooks/useCollaborationColors"
import useIsDesktop from "@hooks/useIsDesktop"

import { TruncatableText } from "@particles"

import { Avatar, BottomDrawer, Tooltip } from "@atoms"
import DrawerHeader from "@atoms/BottomDrawer/DrawerHeader/DrawerHeader"

import { UserAvatar } from "@molecules"

import styles from "./UserAvatarStack.module.scss"
import { UserAvatarStackProps } from "./UserAvatarStack.types"

export function UserListDisplay(
    props: Pick<UserAvatarStackProps, "users" | "avatarSize" | "isLive"> & { mode: "tooltip" | "drawer" },
) {
    const { users, avatarSize, isLive, mode } = props

    const { getUserColor } = useCollaborationColors()
    const { resolvedTheme } = useTheme()

    return (
        <div className={clsx(styles.userListDisplay, styles[mode])}>
            {users.map((user, index) => {
                const liveRingColor = isLive ? (getUserColor(String(user.id)) ?? undefined) : undefined
                return (
                    <div key={user.id ?? `unknown-${index}`} className={styles.userListItem}>
                        <UserAvatar
                            user={user}
                            size={avatarSize}
                            isLive={isLive}
                            liveRingColor={liveRingColor}
                            baseClassName={resolvedTheme === "light" ? "dark-theme" : "light-theme"}
                        />
                        <span>{user.full_name}</span>
                    </div>
                )
            })}
        </div>
    )
}

export default function UserAvatarStack(props: UserAvatarStackProps) {
    const { users, avatarSize, limit, variation = "inline", isLive = false, tooltipPlacement } = props

    const count = users.length

    const remainingUsers = users.slice(limit - 1)

    const isDesktop = useIsDesktop()
    const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false)
    const { getUserColor } = useCollaborationColors()

    const openHint = () => (!isDesktop ? setIsUserDrawerOpen(true) : null)

    const UserList = (
        <UserListDisplay
            users={users}
            avatarSize={avatarSize}
            isLive={isLive}
            mode={isDesktop ? "tooltip" : "drawer"}
        />
    )

    return (
        <Tooltip body={UserList} isDisabled={variation !== "inline" || !isDesktop} defaultPlacement={tooltipPlacement}>
            <div
                className={clsx(styles.base, styles[`avatarSize${avatarSize}`], {
                    [styles.inline]: variation === "inline",
                    [styles.stack]: variation === "stack",
                })}
                onClick={openHint}
            >
                {users.map((user, index) => {
                    const oneIndexedIndex = index + 1 // To match 1-indexed limit prop

                    if (limit < oneIndexedIndex) {
                        return null
                    } else if (limit === oneIndexedIndex && limit < count) {
                        // The stack variation lists the hidden users behind the "+N" row on hover.
                        const hiddenUserList =
                            variation === "stack" ? (
                                <UserListDisplay
                                    users={remainingUsers}
                                    avatarSize={avatarSize}
                                    isLive={isLive}
                                    mode="tooltip"
                                />
                            ) : undefined

                        return (
                            <Fragment key="overflow-count">
                                <Avatar
                                    variant="user-count"
                                    size={avatarSize}
                                    count={count - limit + 1} // Add one to account for the user replaced by this counter
                                    countShowPlus={limit > 1}
                                    isOverlapping={variation === "inline"}
                                    tooltip={hiddenUserList}
                                />

                                {variation === "stack" && (
                                    <TruncatableText
                                        className={styles.avatarName}
                                        text={remainingUsers.map((user) => user.full_name).join(", ")}
                                        alwaysShowTooltip={true}
                                        tooltipOverride={hiddenUserList}
                                    />
                                )}
                            </Fragment>
                        )
                    } else {
                        const liveRingColor = isLive ? (getUserColor(String(user.id)) ?? undefined) : undefined

                        return (
                            <Fragment key={user.id ?? `unknown-${index}`}>
                                {user.isUnknown ? (
                                    <Avatar
                                        variant="user-placeholder"
                                        size={avatarSize}
                                        isOverlapping={variation === "inline"}
                                    />
                                ) : (
                                    <UserAvatar
                                        user={user}
                                        size={avatarSize}
                                        isOverlapping={variation === "inline"}
                                        isLive={isLive}
                                        liveRingColor={liveRingColor}
                                    />
                                )}

                                {variation === "stack" && (
                                    <TruncatableText
                                        className={clsx(styles.avatarName, {
                                            [styles.unknown]: user.isUnknown,
                                        })}
                                        text={user.full_name}
                                        tooltipOnTruncate={true}
                                    />
                                )}
                            </Fragment>
                        )
                    }
                })}
            </div>
            <BottomDrawer.Root isOpen={isUserDrawerOpen} onOpenChange={setIsUserDrawerOpen}>
                <BottomDrawer.Content>
                    <DrawerHeader.Root />
                    {UserList}
                </BottomDrawer.Content>
            </BottomDrawer.Root>
        </Tooltip>
    )
}
